import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetAdvanceQuery,
  useCreateAdvanceMutation,
  useUpdateAdvanceMutation,
  useGetEmployeesQuery,
} from '../../../store/api/hrmsApi';

function AdvanceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    employeeId: '',
    amount: '',
    date: null,
    reason: '',
    status: 'Pending',
  });

  const { data: advance, isLoading: isLoadingAdvance } = useGetAdvanceQuery(id, {
    skip: !isEdit,
  });

  // Fetch all active employees for the dropdown. 
  // For a large system, an autocomplete with search would be better, but a select is fine for MVP.
  const { data: employeesData } = useGetEmployeesQuery({ limit: 1000 });
  const employees = employeesData?.employees || [];

  const [createAdvance, { isLoading: isCreating }] = useCreateAdvanceMutation();
  const [updateAdvance, { isLoading: isUpdating }] = useUpdateAdvanceMutation();

  useEffect(() => {
    if (isEdit && advance) {
      setFormData({
        employeeId: advance.employeeId || '',
        amount: advance.amount || '',
        date: advance.date ? new Date(advance.date) : null,
        reason: advance.reason || '',
        status: advance.status || 'Pending',
      });
    }
  }, [advance, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await updateAdvance({ id, ...formData }).unwrap();
      } else {
        await createAdvance(formData).unwrap();
      }
      navigate('/hrms/advances');
    } catch (error) {
      console.error('Failed to save advance:', error);
      alert('Error saving advance. Please check the console.');
    }
  };

  if (isEdit && isLoadingAdvance) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Advance' : 'Record Advance'}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/hrms/advances')}>
          Cancel
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{xs: 12, sm: 6}}>
                <FormControl fullWidth required>
                  <InputLabel>Employee</InputLabel>
                  <Select
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleChange}
                    label="Employee"
                  >
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.firstName} {emp.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date"
                    value={formData.date}
                    onChange={(newValue) => {
                      setFormData((prev) => ({ ...prev, date: newValue }));
                    }}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Deducted">Deducted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Reason / Notes"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid size={{xs: 12}}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isCreating || isUpdating}
                  sx={{ mt: 2 }}
                >
                  {isEdit ? 'Update Advance' : 'Save Advance'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default AdvanceForm;
